import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  ContentSelectComboBoxForForm2,
  ListBoxModifier,
} from '../../../../components/molecules/Form/components/ComboBox';
import { Hint } from '../../../../_lib/components/Hint';
import { BlockType } from '../../../../types/api/ElementType';
import { useBlockContext } from '../../../../_lib/BlockFetcher';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import {
  ComboBoxParam,
  DateRangeWithIsAllDaySwitch,
  HiddenParam,
  Input,
  Row,
  Textarea,
  TextAreaParam,
  TextParam,
} from '../../../../_lib/Form2';
import { HiddenInput } from '../../../../_lib/Form2/elements/HiddenInput/HiddenInput';
import { formParamsUtils } from '../../../../_lib/Form2/utils/formParamsUtils';
import { DateRangeParam } from '../../../../_lib/Form2/types/DateRangeParam';

export type InformationParams =
  | HiddenParam<'id'>
  | TextParam<'title'>
  | TextAreaParam<'text'>
  | DateRangeParam<'daterange'>
  | ComboBoxParam<'referencePageId'>;

export default function Information({ params }: { params: ReturnType<typeof formParamsUtils<InformationParams>> }) {
  const { t } = useSonTranslation('TopHeadlinesForm');
  const { elementType } = useBlockContext();

  const [hintMessage, setHintMessage] = useState<string | undefined>(undefined);

  const id = params.findByName('id');
  const title = params.getByName('title');
  const text = params.getByName('text');
  const daterange = params.getByName('daterange');
  const referencePageId = params.getByName('referencePageId');

  /**
   * Hint messages are added/removed on blur.
   * Informs user about the validity of topheadlines and if they will be shown in
   * topheadlines or topheadlinesarchive accordingly.
   */
  const giveMeAHint = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    const today = dayjs().format('YYYY-MM-DD');
    const endDateValue = event.target.value;

    if (elementType === BlockType.pagemanagement && today > endDateValue) {
      setHintMessage(t('daterange.hint.fromParentPageToArchive'));
    } else if (elementType === BlockType.topheadlinesarchive && today <= endDateValue) {
      setHintMessage(t('daterange.hint.fromArchiveToParentPage'));
    } else if (elementType === BlockType.topheadlines && today > endDateValue) {
      setHintMessage(t('daterange.hint.fromParentPageToArchive'));
    } else {
      setHintMessage(undefined);
    }
  };

  const idSuffix = 'topheadline';

  return (
    <>
      {id ? <HiddenInput {...id} /> : null}
      <Row>
        <Input key={`${title.name}-${idSuffix}`} label={t('title')} param={title} />
      </Row>
      <Row>
        <Textarea key={`${text.name}-${idSuffix}`} label={t('textAreaTitle')} param={text} />
      </Row>
      <ContentSelectComboBoxForForm2
        labelTitle={t('referencePageId.label')}
        labelHelpText={t('referencePageId.labelHelpText') as string}
        suggestUrl={referencePageId.comboBoxUrl?.url}
        modifierListBox={ListBoxModifier.RELATIVE}
        name={referencePageId.name}
        selected={referencePageId.selected}
      />
      <DateRangeWithIsAllDaySwitch
        labelStart={t('daterange.from')}
        labelEnd={t('daterange.to')}
        labelSwitch={t('daterange.isAllDay')}
        dateRangeParam={daterange}
        onBlurEnd={giveMeAHint}
      />
      {/* Live region must be rendered anyway, otherwise hint is not announced in screen readers! */}
      <LiveRegion invisible={false} htmlTag="div">
        {hintMessage && (
          <div className="top-headlines-form__hint">
            <Hint message={hintMessage} />
          </div>
        )}
      </LiveRegion>
    </>
  );
}
